import type { DemoDatabase } from '../schema';
import type { PharmacyArea, PharmacyStore, ShiftAlert, ShiftAssignment, ShiftPosition, ShiftStaff } from '../types';

// ─── 期間（デモの基準月） ────────────────────────────────────
// シフト応援要請（coverage rows）/ 休暇申請デモと同じ 2026-05 に揃える。
const SEED_YEAR = 2026;
const SEED_MONTH0 = 4; // 0-indexed: 5月

/** デモ基準年（mock 読み取り層が参照）。 */
export const DEMO_SHIFT_YEAR = SEED_YEAR;
/** デモ基準月（0-indexed）。 */
export const DEMO_SHIFT_MONTH0 = SEED_MONTH0;
/** 月次集計のフィルタ用プレフィックス（例: "2026-05"）。 */
export const DEMO_SHIFT_MONTH_PREFIX = `${SEED_YEAR}-${String(SEED_MONTH0 + 1).padStart(2, '0')}`;
/** 詳細画面の初期アンカー日。 */
export const DEMO_SHIFT_ANCHOR = `${DEMO_SHIFT_MONTH_PREFIX}-15`;

/** YYYY-MM-DD（UTC ベース、attendanceOpPackages の toYmdUtc と同方針）。 */
function ymd(year: number, month0: number, day: number): string {
  const m = String(month0 + 1).padStart(2, '0');
  const d = String(day).padStart(2, '0');
  return `${year}-${m}-${d}`;
}

/** 月の日数。 */
function daysInMonth(year: number, month0: number): number {
  return new Date(Date.UTC(year, month0 + 1, 0)).getUTCDate();
}

/** 曜日 0=月 … 6=日（Date.getUTCDay は 0=日なので変換）。 */
function dowMon(year: number, month0: number, day: number): number {
  return (new Date(Date.UTC(year, month0, day)).getUTCDay() + 6) % 7;
}

// ─── エリア（組織ユニット） ──────────────────────────────────
const AREAS: readonly PharmacyArea[] = [{
  id: 'area-tokyo',
  name: '東京エリア',
  sortOrder: 0
}, {
  id: 'area-kanagawa',
  name: '神奈川エリア',
  sortOrder: 1
}, {
  id: 'area-saitama-chiba',
  name: '埼玉・千葉エリア',
  sortOrder: 2
}];

// ─── 店舗（拠点） ────────────────────────────────────────────
const STORES: readonly PharmacyStore[] = [
// 渋谷店 = フォーカス店舗（1ヶ月分のシフトを厚く seed）
{
  id: 'store-shibuya',
  name: '渋谷店',
  code: 'SBY',
  areaId: 'area-tokyo',
  openHour: 9,
  closeHour: 22,
  monthlyLaborBudget: 3_600_000,
  requiredHeadcount: 3,
  sortOrder: 0
}, {
  id: 'store-shinjuku',
  name: '新宿店',
  code: 'SJK',
  areaId: 'area-tokyo',
  openHour: 9,
  closeHour: 22,
  monthlyLaborBudget: 4_000_000,
  requiredHeadcount: 3,
  sortOrder: 1
}, {
  id: 'store-ikebukuro',
  name: '池袋店',
  code: 'IKB',
  areaId: 'area-tokyo',
  openHour: 9,
  closeHour: 22,
  monthlyLaborBudget: 3_400_000,
  requiredHeadcount: 3,
  sortOrder: 2
}, {
  id: 'store-yokohama',
  name: '横浜店',
  code: 'YKH',
  areaId: 'area-kanagawa',
  openHour: 10,
  closeHour: 21,
  monthlyLaborBudget: 3_000_000,
  requiredHeadcount: 2,
  sortOrder: 3
}, {
  id: 'store-kawasaki',
  name: '川崎店',
  code: 'KWS',
  areaId: 'area-kanagawa',
  openHour: 10,
  closeHour: 21,
  monthlyLaborBudget: 2_600_000,
  requiredHeadcount: 2,
  sortOrder: 4
}, {
  id: 'store-omiya',
  name: '大宮店',
  code: 'OMY',
  areaId: 'area-saitama-chiba',
  openHour: 10,
  closeHour: 21,
  monthlyLaborBudget: 2_800_000,
  requiredHeadcount: 2,
  sortOrder: 5
}, {
  id: 'store-chiba',
  name: '千葉店',
  code: 'CHB',
  areaId: 'area-saitama-chiba',
  openHour: 10,
  closeHour: 21,
  monthlyLaborBudget: 2_500_000,
  requiredHeadcount: 2,
  sortOrder: 6
}];

// ─── スタッフ + 週次パターン ────────────────────────────────
type PatternEntry = {
  /** 曜日 0=月 … 6=日 */
  dows: number[];
  startH: number;
  endH: number;
  breakStartH?: number;
  breakEndH?: number;
};
type StaffSeed = {
  id: string;
  name: string;
  initials: string;
  position: ShiftPosition;
  qualified: boolean;
  employmentType: ShiftStaff['employmentType'];
  hourlyWage: number;
  pattern: PatternEntry[];
};

// 渋谷店（フォーカス）: 元 work-schedule の人物・職種を踏襲。
// 週末夜間に薬剤師が不在 → 無資格販売リスク、店長は長時間 → 残業超過、という設計。
const SHIBUYA_STAFF: readonly StaffSeed[] = [{
  id: 'staff-sby-ph1',
  name: '田中 美咲',
  initials: '田',
  position: 'pharmacist',
  qualified: true,
  employmentType: 'full_time',
  hourlyWage: 2200,
  // 本人スケジュール画面のデモ用に曜日ごとへ時間を散らす（週末夜間は不在のまま）。
  pattern: [{
    dows: [0],
    startH: 9,
    endH: 18,
    breakStartH: 12,
    breakEndH: 13
  }, {
    dows: [1],
    startH: 11,
    endH: 20,
    breakStartH: 15,
    breakEndH: 16
  }, {
    dows: [4],
    startH: 9,
    endH: 15,
    breakStartH: 12,
    breakEndH: 13
  }, {
    dows: [5],
    startH: 10,
    endH: 17,
    breakStartH: 13,
    breakEndH: 14
  }, {
    dows: [6],
    startH: 9,
    endH: 14
  }]
}, {
  id: 'staff-sby-ph2',
  name: '斎藤 拓也',
  initials: '斎',
  position: 'pharmacist',
  qualified: true,
  employmentType: 'part_time',
  hourlyWage: 1900,
  pattern: [{
    dows: [2, 3],
    startH: 9,
    endH: 16,
    breakStartH: 12,
    breakEndH: 13
  }]
}, {
  id: 'staff-sby-rs1',
  name: '鈴木 健太',
  initials: '鈴',
  position: 'registeredSeller',
  qualified: true,
  employmentType: 'full_time',
  hourlyWage: 1600,
  pattern: [{
    dows: [0, 1, 4],
    startH: 16,
    endH: 22
  }]
}, {
  id: 'staff-sby-rs2',
  name: '佐藤 由香',
  initials: '佐',
  position: 'registeredSeller',
  qualified: true,
  employmentType: 'part_time',
  hourlyWage: 1400,
  pattern: [{
    dows: [2, 3, 5],
    startH: 9,
    endH: 15
  }]
}, {
  id: 'staff-sby-rs3',
  name: '吉田 浩（店長）',
  initials: '吉',
  position: 'registeredSeller',
  qualified: true,
  employmentType: 'full_time',
  hourlyWage: 1800,
  pattern: [{
    dows: [0, 1, 2, 3, 4, 5],
    startH: 9,
    endH: 22,
    breakStartH: 13,
    breakEndH: 14
  }]
}, {
  id: 'staff-sby-ca1',
  name: '伊藤 彩',
  initials: '伊',
  position: 'cashier',
  qualified: false,
  employmentType: 'part_time',
  hourlyWage: 1200,
  pattern: [{
    dows: [0, 1, 4, 5, 6],
    startH: 9,
    endH: 16
  }]
}, {
  id: 'staff-sby-ca2',
  name: '渡辺 翔',
  initials: '渡',
  position: 'cashier',
  qualified: false,
  employmentType: 'part_time',
  hourlyWage: 1200,
  pattern: [{
    dows: [0, 1, 4, 5, 6],
    startH: 16,
    endH: 22
  }]
}, {
  id: 'staff-sby-co1',
  name: '中村 結衣',
  initials: '結',
  position: 'cosmetics',
  qualified: false,
  employmentType: 'full_time',
  hourlyWage: 1500,
  pattern: [{
    dows: [0, 1, 2, 4, 5],
    startH: 10,
    endH: 18,
    breakStartH: 13,
    breakEndH: 14
  }]
}, {
  id: 'staff-sby-st1',
  name: '山本 直樹',
  initials: '山',
  position: 'stocker',
  qualified: false,
  employmentType: 'part_time',
  hourlyWage: 1100,
  pattern: [{
    dows: [0, 2, 4],
    startH: 9,
    endH: 14
  }]
}];

/** 他店舗用: 職種を一通り揃えた軽量スタッフ（一覧の集計・人件費が成立する程度）。 */
function makeBasicStaff(storeId: string): StaffSeed[] {
  const p = storeId.replace('store-', '');
  return [{
    id: `staff-${p}-ph1`,
    name: '管理薬剤師',
    initials: '薬',
    position: 'pharmacist',
    qualified: true,
    employmentType: 'full_time',
    hourlyWage: 2100,
    pattern: [{
      dows: [0, 1, 2, 3, 4],
      startH: 10,
      endH: 18,
      breakStartH: 13,
      breakEndH: 14
    }]
  }, {
    id: `staff-${p}-rs1`,
    name: '登録販売者A',
    initials: '登',
    position: 'registeredSeller',
    qualified: true,
    employmentType: 'full_time',
    hourlyWage: 1500,
    pattern: [{
      dows: [0, 1, 4, 5, 6],
      startH: 14,
      endH: 21
    }]
  }, {
    id: `staff-${p}-ca1`,
    name: 'レジ担当A',
    initials: 'レ',
    position: 'cashier',
    qualified: false,
    employmentType: 'part_time',
    hourlyWage: 1150,
    pattern: [{
      dows: [0, 1, 2, 4, 5, 6],
      startH: 10,
      endH: 16
    }]
  }, {
    id: `staff-${p}-ca2`,
    name: 'レジ担当B',
    initials: 'レ',
    position: 'cashier',
    qualified: false,
    employmentType: 'part_time',
    hourlyWage: 1150,
    pattern: [{
      dows: [1, 3, 5, 6],
      startH: 15,
      endH: 21
    }]
  }];
}

/** スタッフの週次パターンから、対象月の全 assignment を生成。 */
function buildAssignments(storeId: string, staffList: readonly StaffSeed[]): {
  staff: ShiftStaff[];
  assignments: ShiftAssignment[];
} {
  const staff: ShiftStaff[] = staffList.map(s => ({
    id: s.id,
    storeId,
    name: s.name,
    initials: s.initials,
    position: s.position,
    qualified: s.qualified,
    employmentType: s.employmentType,
    hourlyWage: s.hourlyWage
  }));
  const assignments: ShiftAssignment[] = [];
  const total = daysInMonth(SEED_YEAR, SEED_MONTH0);
  for (let day = 1; day <= total; day++) {
    const dow = dowMon(SEED_YEAR, SEED_MONTH0, day);
    const date = ymd(SEED_YEAR, SEED_MONTH0, day);
    for (const s of staffList) {
      for (const pat of s.pattern) {
        if (!pat.dows.includes(dow)) continue;
        assignments.push({
          id: `asg-${s.id}-${date}`,
          storeId,
          staffId: s.id,
          date,
          startH: pat.startH,
          endH: pat.endH,
          breakStartH: pat.breakStartH ?? null,
          breakEndH: pat.breakEndH ?? null,
          position: s.position,
          note: ''
        });
      }
    }
  }
  return {
    staff,
    assignments
  };
}

// ─── アラート（4種） ────────────────────────────────────────
// 一覧の行バッジ・バナー、詳細日次のリスクバナー、応援要請の起点になる。
function buildAlerts(): ShiftAlert[] {
  const createdAt = `${SEED_YEAR}-05-14T09:00:00.000Z`;
  const base: Array<Omit<ShiftAlert, 'id' | 'storeName' | 'createdAt'>> = [{
    storeId: 'store-shibuya',
    type: 'unqualified_sales_risk',
    severity: 'critical',
    date: ymd(SEED_YEAR, SEED_MONTH0, 17),
    startH: 18,
    endH: 22,
    position: 'pharmacist',
    message: '5/17(土) 18:00–22:00 は薬剤師・登録販売者が不在の見込みです。第1類・第2類医薬品の販売ができません（無資格販売リスク）。',
    status: 'open',
    linkedCoverageRequestId: null
  }, {
    storeId: 'store-chiba',
    type: 'unqualified_sales_risk',
    severity: 'critical',
    date: ymd(SEED_YEAR, SEED_MONTH0, 17),
    startH: 17,
    endH: 21,
    position: 'registeredSeller',
    message: '5/17(土) 17:00–21:00 は有資格者が不在の見込みです（無資格販売リスク）。',
    status: 'open',
    linkedCoverageRequestId: null
  }, {
    storeId: 'store-ikebukuro',
    type: 'understaffed',
    severity: 'warning',
    date: ymd(SEED_YEAR, SEED_MONTH0, 16),
    startH: 14,
    endH: 18,
    position: 'cashier',
    message: '5/16(金) 14:00–18:00 はレジ担当が必要人数（2名）に対し1名で、シフトが埋まっていません。',
    status: 'open',
    linkedCoverageRequestId: null
  }, {
    storeId: 'store-kawasaki',
    type: 'understaffed',
    severity: 'warning',
    date: ymd(SEED_YEAR, SEED_MONTH0, 23),
    startH: 18,
    endH: 21,
    position: 'registeredSeller',
    message: '5/23(金) 18:00–21:00 は登録販売者のシフトが埋まっていません。',
    status: 'open',
    linkedCoverageRequestId: null
  }, {
    storeId: 'store-yokohama',
    type: 'labor_cost_over',
    severity: 'warning',
    date: ymd(SEED_YEAR, SEED_MONTH0, 1),
    startH: null,
    endH: null,
    position: null,
    message: '5月の人件費が予算を約8%超過する見込みです。',
    status: 'open',
    linkedCoverageRequestId: null
  }, {
    storeId: 'store-shibuya',
    type: 'overtime_over',
    severity: 'critical',
    date: ymd(SEED_YEAR, SEED_MONTH0, 1),
    startH: null,
    endH: null,
    position: 'registeredSeller',
    message: '吉田 浩（店長）の残業見込みが月45時間の上限を超過します。',
    status: 'open',
    linkedCoverageRequestId: null
  }];
  const storeName = (id: string): string => STORES.find(s => s.id === id)?.name ?? id;
  return base.map((a, i) => ({
    ...a,
    id: `shift-alert-${i + 1}`,
    storeName: storeName(a.storeId),
    createdAt
  }));
}

/** 薬局シフト管理 mock データを seed する（冪等：店舗が無いときのみ投入）。 */
export async function seedPharmacyShift(db: DemoDatabase): Promise<void> {
  const n = await db.pharmacyStores.count();
  if (n > 0) return;
  await db.pharmacyAreas.bulkAdd([...AREAS]);
  await db.pharmacyStores.bulkAdd([...STORES]);
  const allStaff: ShiftStaff[] = [];
  const allAssignments: ShiftAssignment[] = [];
  for (const store of STORES) {
    const staffSeed = store.id === 'store-shibuya' ? SHIBUYA_STAFF : makeBasicStaff(store.id);
    const {
      staff,
      assignments
    } = buildAssignments(store.id, staffSeed);
    allStaff.push(...staff);
    allAssignments.push(...assignments);
  }
  await db.shiftStaff.bulkAdd(allStaff);
  await db.shiftAssignments.bulkAdd(allAssignments);
  await db.shiftAlerts.bulkAdd(buildAlerts());
}